import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { DocumentPreviewSheet } from '@/components/documents/DocumentPreviewSheet';
import type { Tables } from '@/integrations/supabase/types';

type Document = Tables<"documents">;

interface DocumentPreviewContextType {
  openDocument: (doc: Document) => void;
  closeDocument: () => void;
  currentDocument: Document | null;
}

const DocumentPreviewContext = createContext<DocumentPreviewContextType | null>(null);

export function DocumentPreviewProvider({ children }: { children: ReactNode }) {
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);

  const openDocument = useCallback((doc: Document) => {
    setPreviewDoc(doc);
  }, []);

  const closeDocument = useCallback(() => {
    setPreviewDoc(null);
  }, []);

  return (
    <DocumentPreviewContext.Provider value={{ openDocument, closeDocument, currentDocument: previewDoc }}>
      {children}
      <DocumentPreviewSheet
        document={previewDoc}
        open={!!previewDoc}
        onOpenChange={(open) => !open && closeDocument()}
      />
    </DocumentPreviewContext.Provider>
  );
}

export function useDocumentPreview() {
  const context = useContext(DocumentPreviewContext);
  if (!context) {
    throw new Error('useDocumentPreview must be used within a DocumentPreviewProvider');
  }
  return context;
}
